import { useEffect, useState } from 'react';
import { IconArrowLeft, IconBell, IconChevronLeft, IconChevronRight, IconDownload, IconLock, IconPlus, IconTargetArrow, IconLayoutSidebarRight } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { KPI_AVG, KPI_DEPTS, KPI_GAUGES, KPI_GRADES, KPI_GRADE_NOTE, KPI_ROWS, KPI_STAFF, KPI_TABS, KPI_UNSCORED, fmtVi, gaugeArc, gradeShare, type Grade, type KpiTab } from './kpi';
import { HrKpiDock } from './HrKpiDock';
import { HrKpiDept, useDeptSegs } from './HrKpiDept';
import { HrKpiClose } from './HrKpiClose';
import { HrKpiRules, useRuleSegs } from './HrKpiRules';
import { HrKpiMine } from './HrKpiMine';
import { HrKpiPerson, usePersonSegs } from './HrKpiPerson';
import { useT } from '@hana/i18n';
import './i18n';
import s from './kpi.module.css';

// Mint-KPI · tab Công ty (design-specs/Mint-KPI.md). Chỉ đọc; nút ghi báo toast. Số job / cut-off / hạng — không tiền.

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

const GRADE_CLS: Record<Grade, string> = { A: s.gA ?? '', B: s.gB ?? '', C: s.gC ?? '', D: s.gD ?? '' };
const GRADE_COLOR: Record<Grade, string> = { A: 'var(--ok)', B: 'var(--d-blue)', C: 'var(--d-amber)', D: 'var(--err)' };
const GAUGE_COLOR = { ok: 'var(--ok)', blue: 'var(--d-blue)', amber: 'var(--d-amber)' } as const;
const GRADES: Grade[] = ['A', 'B', 'C', 'D'];

export function HrKpi() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const [tab, setTab] = useState<KpiTab>('co');
  const dept = useDeptSegs();
  const person = usePersonSegs();
  const rule = useRuleSegs();
  const [mode, setMode] = useState<'m' | 'q'>('m');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  const [right, setRight] = useState(true);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const noWrite = () => toast({ msg: t('hr.noWrite') });
  const pickTab = (k: KpiTab) => (k === 'co' || k === 'dept' || k === 'staff' || k === 'mine' || k === 'close' || k === 'rules' ? setTab(k) : toast({ msg: t('hr.notYet') }));
  const share = gradeShare(KPI_GRADES);
  const maxAvg = 100;
  const ring = (() => {
    let acc = 0;
    const stops = GRADES.filter((g) => KPI_GRADES[g] > 0).map((g) => {
      const from = acc;
      acc += share[g];
      return `${GRADE_COLOR[g]} ${from}% ${acc}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  })();

  return (
    <Page kind="table" dock={wide && right && forced !== 'noperm' ? <HrKpiDock onClose={() => setRight(false)} onNote={(m) => toast({ msg: m })} /> : undefined}>
      <PageHeader
        title={t('hr.kpi.title')}
        lead={<HeaderTool label={t('hr.backOverview')} icon={IconArrowLeft} onClick={() => void navigate({ to: '/ezy/hr' })} />}
        meta={<><span className={s.chip}>Oct/2026</span><span className={s.live}>{t('hr.kpi.live')}</span></>}
        actions={
          <>
            <Segmented kind="toggle" label={t('hr.kpi.cycle')} value={mode} onChange={setMode} items={[{ key: 'm', label: t('hr.kpi.month') }, { key: 'q', label: t('hr.kpi.quarter') }]} />
            <div className={s.period} role="group" aria-label={t('hr.kpi.period')}>
              <button type="button" aria-label={t('hr.kpi.prev')} title={t('hr.kpi.prev')} onClick={() => toast({ msg: t('hr.kpi.sampleOnly') })}><IconChevronLeft size={16} aria-hidden="true" /></button>
              <span>Oct/2026</span>
              <button type="button" aria-label={t('hr.kpi.next')} title={t('hr.kpi.next')} onClick={() => toast({ msg: t('hr.kpi.sampleOnly') })}><IconChevronRight size={16} aria-hidden="true" /></button>
            </div>
            <Segmented kind="toggle" label={t('hr.kpi.density')} value={dense} onChange={setDense} items={[{ key: 'std', label: t('hr.kpi.std') }, { key: 'compact', label: t('hr.kpi.compact') }]} />
            <HeaderTool label={t('hr.kpi.notifTool')} icon={IconBell} n={4} onClick={() => toast({ msg: t('hr.kpi.notif') })} />
            <HeaderTool label={t('hr.kpi.openRight')} icon={IconLayoutSidebarRight} active={right} onClick={() => setRight((v) => !v)} />
            {tab === 'mine' || tab === 'close' ? null : <Button icon={<IconLock size={16} aria-hidden="true" />} onClick={() => setTab('close')}>{t('hr.kpi.close')}</Button>}
            <Button variant="primary" icon={tab === 'rules' ? <IconPlus size={16} aria-hidden="true" /> : <IconDownload size={16} aria-hidden="true" />} onClick={noWrite}>{tab === 'rules' ? t('hr.kpi.addRule') : tab === 'close' ? t('hr.kpi.closeOct') : tab === 'mine' ? t('hr.kpi.exportMine') : t('hr.kpi.export')}</Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabs}>
          <Segmented label={t('hr.kpi.title')} value={tab} onChange={pickTab} items={KPI_TABS} />
          {tab === 'dept' ? dept.segs : tab === 'staff' ? person.segs : tab === 'rules' ? rule.segs : null}
        </div>
        <StateView
          state={forced}
          title={forced === 'empty' ? t('hr.kpi.emptyTitle') : undefined}
          desc={forced === 'empty' ? t('hr.kpi.emptyDesc') : undefined}
          icon={forced === 'empty' ? <IconTargetArrow size={26} stroke={1.8} /> : undefined}
          action={forced === 'error' ? <Button onClick={() => location.reload()}>{t('hr.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {tab === 'dept' ? <HrKpiDept pos={dept.pos} view={dept.view} onNote={(m) => toast({ msg: m })} /> : tab === 'staff' ? <HrKpiPerson pf={person.pf} onNote={(m) => toast({ msg: m })} /> : tab === 'mine' ? <HrKpiMine onNote={(m) => toast({ msg: m })} /> : tab === 'close' ? <HrKpiClose /> : tab === 'rules' ? <HrKpiRules kind={rule.kind} onNote={(m) => toast({ msg: m })} /> : (<>
          <section className={s.stats} aria-label={t('hr.kpi.metrics')}>
            <div className={s.stat}>
              <div>{t('hr.kpi.scoredStaff')}</div>
              <b>{KPI_STAFF.scored} / {KPI_STAFF.total}</b>
              <div>{KPI_STAFF.note}</div>
            </div>
            <div className={s.stat}>
              <div>{t('hr.kpi.avgCo')}</div>
              <b>{fmtVi(KPI_AVG)}</b>
              <div>{t('hr.kpi.avgNote')}</div>
            </div>
            {GRADES.map((g) => (
              <div key={g} className={s.stat}>
                <div>{t('hr.kpi.grade', { g })}</div>
                <b className={GRADE_CLS[g]}>{KPI_GRADES[g]}</b>
              </div>
            ))}
          </section>

          <div className={s.row}>
            <section className={s.card} aria-label={t('hr.kpi.avgByDept')}>
              <div className={s.cardHd}><h2>{t('hr.kpi.avgByDept')}</h2></div>
              <div className={s.legend}>
                <span><i style={{ background: 'var(--d-blue)' }} />{t('hr.kpi.avgDept')}</span>
                <span><i style={{ background: 'var(--warn)' }} />{t('hr.kpi.avgCoShort')}</span>
                <em>{t('hr.kpi.scale')}</em>
              </div>
              <div className={s.bars}>
                {KPI_DEPTS.map((d) => (
                  <div key={d.k} className={s.bar} style={{ height: `${(d.avg / maxAvg) * 100}%` }} title={`${d.k} · ${fmtVi(d.avg)}`} />
                ))}
                <div className={s.avgLine} style={{ bottom: `${(KPI_AVG / maxAvg) * 100}%` }} />
              </div>
              <div className={s.xs}>{KPI_DEPTS.map((d) => <span key={d.k}>{d.k}</span>)}</div>
            </section>
            <section className={s.card} aria-label={t('hr.kpi.grading')}>
              <div className={s.cardHd}><h2>{t('hr.kpi.grading')}</h2><span>{KPI_GRADE_NOTE}</span></div>
              <div className={s.donut}>
                <div className={s.ring} style={{ background: ring }} role="img" aria-label={t('hr.kpi.gradingKpi')} />
                <div className={s.grades}>
                  {GRADES.map((g) => (
                    <div key={g} style={{ display: 'contents' }}>
                      <span style={{ textAlign: 'left', color: 'var(--tx)', fontSize: 13 }}><i style={{ background: GRADE_COLOR[g] }} />{t('hr.kpi.grade', { g })}</span>
                      <b>{KPI_GRADES[g]}</b>
                      <span>{share[g]}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={s.unscored}>
                <span style={{ color: 'var(--tx2)', fontSize: 13 }}>{KPI_UNSCORED.label}</span>
                <span>{KPI_UNSCORED.who}</span>
                <b>{t('hr.kpi.people', { n: KPI_UNSCORED.n })}</b>
              </div>
            </section>
          </div>

          <section className={s.card} aria-label={t('hr.kpi.coRate')}>
            <div className={s.cardHd}><h2>{t('hr.kpi.coRate')}</h2><span>{t('hr.kpi.noTarget')}</span></div>
            <div className={s.gauges}>
              {KPI_GAUGES.map((g) => (
                <div key={g.k} className={s.gauge}>
                  <svg width="190" height="104" viewBox="0 0 190 104" role="img" aria-label={`${g.k} ${fmtVi(g.pct)}%`}>
                    <path d={gaugeArc(95, 96, 80, 100)} fill="none" stroke="var(--sub)" strokeWidth="16" strokeLinecap="round" />
                    <path d={gaugeArc(95, 96, 80, g.pct)} fill="none" stroke={GAUGE_COLOR[g.tone]} strokeWidth="16" strokeLinecap="round" />
                  </svg>
                  <b>{g.k} · {g.label}</b>
                  <div>{g.sub}</div>
                </div>
              ))}
            </div>
          </section>

          <section className={s.card} aria-label={t('hr.kpi.coMetrics')}>
            <div className={s.cardHd}>
              <h2>{t('hr.kpi.coMetrics')}</h2>
              <span>{t('hr.kpi.noTargetSrc')}</span>
              <Button size="sm" icon={<IconTargetArrow size={16} aria-hidden="true" />} onClick={noWrite}>{t('hr.kpi.setTarget')}</Button>
            </div>
            <table className={s.tbl} aria-label={t('hr.kpi.coMetrics')}>
              <colgroup>
                {['32%', '12%', '14%', '14%', '12%', '16%'].map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">{t('hr.kpi.thMetric')}</th>
                  <th scope="col">{t('hr.kpi.thDept')}</th>
                  <th scope="col" className={s.r}>{t('hr.kpi.thActual')}</th>
                  <th scope="col" className={s.r}>{t('hr.kpi.thTarget')}</th>
                  <th scope="col">{t('hr.kpi.thData')}</th>
                  <th scope="col">{t('hr.kpi.thSource')}</th>
                </tr>
              </thead>
              <tbody>
                {KPI_ROWS.map((r) => (
                  <tr key={r.k}>
                    <td><b>{r.k}</b> · {r.label}</td>
                    <td>{r.dept}</td>
                    <td className={s.r}><b>{r.actual}</b></td>
                    <td className={`${s.r} ${s.mute}`}>{r.target}</td>
                    <td className={r.kind === 'Mẫu' ? s.sample : s.real}>{r.kind}</td>
                    <td className={s.mute}>{r.src}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          </>)}
        </StateView>
      </div>
    </Page>
  );
}
